import {
  carousel,
  CarouselVariantProps,
} from '@repo/themes/styled-system/recipes';
import { ComponentProps, forwardRef, useRef } from 'react';
import useCarousel from '../hooks/useCarousel';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './Button';

type Direction = 'left' | 'right';
type ScrollType = 'item' | 'fixed' | 'full';

export interface CarouselProps
  extends Partial<CarouselVariantProps>,
    ComponentProps<'div'> {
  scrollType?: ScrollType;
  showArrows?: boolean;
}
export interface CarouselContentProps
  extends Partial<CarouselVariantProps>,
    ComponentProps<'div'> {
  scrollType?: ScrollType;
}
export interface CarouselItemProps
  extends Partial<CarouselVariantProps>,
    ComponentProps<'div'> {}

interface CarouselDirectionProps extends ComponentProps<typeof Button> {
  arrowDirection: Direction;
  onClick: () => void;
}

const CarouselDirection = ({
  arrowDirection,
  onClick,
  ...props
}: CarouselDirectionProps) => {
  const isRight = arrowDirection === 'right';
  const Icon = isRight ? ChevronRight : ChevronLeft;

  return (
    <Button variant='navBar' onClick={onClick} {...props}>
      <Icon />
    </Button>
  );
};

const Carousel = ({
  children,
  scrollType,
  showArrows,
  ...allProps
}: CarouselProps) => {
  const [variantProps, props] = carousel.splitVariantProps(allProps);
  const classes = carousel(variantProps);
  const containerRef = useRef<HTMLDivElement>(null);
  const { scroll, isStart, isEnd } = useCarousel({
    containerRef,
    scrollType,
  });

  return (
    <div {...props} className={classes.root}>
      <CarouselDirection
        arrowDirection='left'
        onClick={() => scroll('left')}
        visibility={showArrows && !isStart ? 'visible' : 'hidden'}
      />
      <CarouselContent
        ref={containerRef}
        scrollType={scrollType}
        data-slot='content'
      >
        {children}
      </CarouselContent>
      <CarouselDirection
        arrowDirection='right'
        onClick={() => scroll('right')}
        visibility={showArrows && !isEnd ? 'visible' : 'hidden'}
      />
    </div>
  );
};

const CarouselContent = forwardRef<HTMLDivElement, CarouselContentProps>(
  ({ children, ...allProps }, ref) => {
    const [variantProps, props] = carousel.splitVariantProps(allProps);
    const classes = carousel(variantProps);
    return (
      <div {...props} ref={ref} className={classes.content}>
        {children}
      </div>
    );
  },
);

const CarouselItem = ({ children, ...allProps }: CarouselItemProps) => {
  const [variantProps, props] = carousel.splitVariantProps(allProps);
  const classes = carousel(variantProps);
  return (
    <div {...props} className={classes.item}>
      {children}
    </div>
  );
};

Carousel.displayName = 'Carousel.Container';
CarouselContent.displayName = 'Carousel.Content';
CarouselItem.displayName = 'Carousel.Item';

export default {
  Container: Carousel,
  Content: CarouselContent,
  Item: CarouselItem,
};
